import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgFor, NgIf } from '@angular/common';
import { Subscription } from 'rxjs';

type ProximityUnit = 'km' | 'mi';
type ProximityStrictness = 1 | 2 | 3;

export interface ProximityValue {
  address: string;
  radius: number | null;
  unit: ProximityUnit;
  strictness: ProximityStrictness;
}

interface UnitOption { value: ProximityUnit; label: string; }
interface StrictnessOption { value: ProximityStrictness; label: string; }

@Component({
  selector: 'app-filter-proximity',
  standalone: true,
  imports: [ReactiveFormsModule, NgIf, NgFor],
  templateUrl: './filter-proximity.component.html',
  styleUrl: './filter-proximity.component.scss',
})
export class FilterProximityComponent implements OnInit, OnDestroy {

  @Input() control!: FormControl;
  @Input() required: boolean = false;

  // Veridion documented caps: 500 km / 300 mi.
  private readonly MAX_BY_UNIT: Record<ProximityUnit, number> = { km: 500, mi: 300 };

  readonly UNIT_OPTIONS: UnitOption[] = [
    { value: 'km', label: 'km' },
    { value: 'mi', label: 'mi' },
  ];
  readonly STRICTNESS_OPTIONS: StrictnessOption[] = [
    { value: 1, label: 'Main location' },
    { value: 2, label: 'Secondary locations' },
    { value: 3, label: 'Any location' },
  ];

  proximityForm: FormGroup;
  radiusDisplayValue: string = '';

  unitDropdownOpen = false;
  strictnessDropdownOpen = false;

  private valueChangesSub?: Subscription;
  private controlSub?: Subscription;
  private syncing = false;

  constructor(private fb: FormBuilder, private elRef: ElementRef) {
    this.proximityForm = this.fb.group({
      address:    ['', [Validators.required, Validators.minLength(2)]],
      radius:     [null as number | null, [Validators.required, Validators.min(0.0001)]],
      unit:       ['km' as ProximityUnit, Validators.required],
      strictness: [1 as ProximityStrictness, Validators.required],
    });
  }

  ngOnInit(): void {
    this.syncFromControlValue();

    this.valueChangesSub = this.proximityForm.valueChanges.subscribe(val => {
      if (this.syncing || !this.control) return;

      // Re-clamp radius if a unit change has dropped its ceiling below the
      // current value (e.g. 400 km → switch to mi → clamp to 300).
      const unit = (val?.unit === 'mi' ? 'mi' : 'km') as ProximityUnit;
      let radius = this.coerceNumberOrNull(val?.radius);
      if (radius !== null && radius > this.MAX_BY_UNIT[unit]) {
        radius = this.MAX_BY_UNIT[unit];
        this.radiusDisplayValue = String(radius);
        this.proximityForm.patchValue({ radius }, { emitEvent: false });
      }

      const next: ProximityValue = {
        address:    (val?.address ?? '').toString(),
        radius,
        unit,
        strictness: ([1, 2, 3].includes(val?.strictness as number)
          ? (val?.strictness as ProximityStrictness)
          : 1) as ProximityStrictness,
      };
      this.control.setValue(next, { emitEvent: false });
      this.control.markAsDirty();
    });

    if (this.control) {
      this.controlSub = this.control.valueChanges.subscribe(() => this.syncFromControlValue());
    }
  }

  ngOnDestroy(): void {
    this.valueChangesSub?.unsubscribe();
    this.controlSub?.unsubscribe();
  }

  onRadiusInput(event: Event): void {
    const input = event.target as HTMLInputElement;

    // Strip everything except digits and at most one decimal point.
    let cleaned = input.value.replace(/[^\d.]/g, '');
    const firstDot = cleaned.indexOf('.');
    if (firstDot !== -1) {
      cleaned = cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '');
    }

    // Parse to number; preserve `cleaned` for display so a trailing dot mid-typing
    // (e.g. "5." while the user is heading toward "5.5") survives.
    let num: number | null = cleaned === '' ? null : Number(cleaned);
    if (num !== null && !Number.isFinite(num)) num = null;

    // Clamp to [0, max-for-current-unit].
    const unit = this.proximityForm.value.unit as ProximityUnit;
    if (num !== null) {
      if (num < 0) {
        num = 0;
        cleaned = '0';
      } else if (num > this.MAX_BY_UNIT[unit]) {
        num = this.MAX_BY_UNIT[unit];
        cleaned = String(num);
      }
    }

    this.radiusDisplayValue = cleaned;
    this.proximityForm.get('radius')?.setValue(num, { emitEvent: true });
    input.value = this.radiusDisplayValue;
  }

  toggleUnitDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.unitDropdownOpen = !this.unitDropdownOpen;
    if (this.unitDropdownOpen) this.strictnessDropdownOpen = false;
  }

  toggleStrictnessDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.strictnessDropdownOpen = !this.strictnessDropdownOpen;
    if (this.strictnessDropdownOpen) this.unitDropdownOpen = false;
  }

  selectUnit(unit: ProximityUnit): void {
    this.proximityForm.patchValue({ unit });
    this.unitDropdownOpen = false;
  }

  selectStrictness(strictness: ProximityStrictness): void {
    this.proximityForm.patchValue({ strictness });
    this.strictnessDropdownOpen = false;
  }

  strictnessLabel(value: ProximityStrictness | null | undefined): string {
    return this.STRICTNESS_OPTIONS.find(o => o.value === value)?.label ?? 'Main location';
  }

  maxForCurrentUnit(): number {
    return this.MAX_BY_UNIT[this.proximityForm.value.unit as ProximityUnit];
  }

  @HostListener('document:click', ['$event.target'])
  onDocumentClick(target: EventTarget | null): void {
    if (!this.unitDropdownOpen && !this.strictnessDropdownOpen) return;
    if (target instanceof Node && !this.elRef.nativeElement.contains(target)) {
      this.unitDropdownOpen = false;
      this.strictnessDropdownOpen = false;
    }
  }

  private syncFromControlValue(): void {
    if (!this.control) return;
    const value = this.control.value as ProximityValue | null | undefined;
    if (!value || typeof value !== 'object') return;
    this.syncing = true;
    try {
      const next = {
        address:    typeof value.address === 'string' ? value.address : '',
        radius:     this.coerceNumberOrNull(value.radius),
        unit:       (value.unit === 'mi' ? 'mi' : 'km') as ProximityUnit,
        strictness: ([1, 2, 3].includes(value.strictness as number)
          ? (value.strictness as ProximityStrictness)
          : 1) as ProximityStrictness,
      };
      this.proximityForm.setValue(next, { emitEvent: false });
      this.radiusDisplayValue = next.radius === null ? '' : String(next.radius);
    } finally {
      this.syncing = false;
    }
  }

  private coerceNumberOrNull(v: unknown): number | null {
    if (v === null || v === undefined || v === '') return null;
    const n = typeof v === 'string' ? Number(v) : (v as number);
    return Number.isFinite(n) ? n : null;
  }
}
